import React, { useState, useEffect } from 'react';
import { sanitizePersonName, validateFullName } from '../../utils/fullName';
import { X, Loader2, Search, AlertTriangle } from 'lucide-react';
import { cn } from '../../utils/cn';
import { condoApi } from '../../services/api';
import { formatCPF } from '../../utils/cpfFormatter';
import { normalizeCPF } from '../../utils/cpfUtils';
import VisitGradeInput, { VisitDaySchedule } from '../VisitGradeInput';
import {
  LiberacaoMode,
  LiberacaoCategory,
  buildLiberacaoRolePayload,
  validateLiberacaoDraft,
} from './liberacaoPayload';

/**
 * "Liberar acesso" from the unit page — the portaria's counterpart to the morador app's
 * AddVisitorSheet, with the same three modes: visita única, período com grade, irrestrito.
 *
 * A resident could pre-clear a visitor from their phone; the portaria could not do it at all, so a
 * resident calling the guardhouse to say "libere o pintor para a semana que vem" had nowhere to be
 * recorded. Everything written here is the shape the app writes, so both show up in both places.
 */

export interface LiberarAcessoModalProps {
  open: boolean;
  customerId: string;
  unitId: string;
  unitDisplay?: string;
  unitNumber?: string;
  blockId?: string;
  blockName?: string;
  /** Resident who authorised it, when the operator picked one. */
  authorizedByResidentId?: string;
  authorizedByName?: string;
  onClose: () => void;
  onCreated: () => void;
  lightTheme?: boolean;
}

/**
 * Which field a validation message is about, so the form can take the operator to it.
 *
 * Matched on the message because validateLiberacaoDraft is shared with the app and returns the
 * sentence a person reads, not a code. Returning nothing is fine — the message still shows in the
 * footer; the scroll is the bonus.
 */
const fieldForProblem = (problem: string, mode: LiberacaoMode): string | undefined => {
  const p = problem.toLowerCase();
  if (p.includes('dia da visita')) return 'liberar-dia';
  if (p.includes('data inicial')) return 'liberar-inicio';
  if (p.includes('data final')) return 'liberar-fim';
  if (p.includes('horários da grade') || p.includes('horário final')) return undefined;
  if (p.includes('futura') || p.includes('6 meses') || p.includes('período')) {
    return mode === 'single' ? 'liberar-dia' : mode === 'irrestrito' ? 'liberar-ate' : 'liberar-fim';
  }
  return undefined;
};

const MODES: { key: LiberacaoMode; label: string; hint: string }[] = [
  { key: 'single', label: 'Visita única', hint: 'Válido para uma única entrada' },
  { key: 'periodo', label: 'Período com grade', hint: 'Dias e horários específicos, por até 6 meses' },
  { key: 'irrestrito', label: 'Irrestrito', hint: 'Pode entrar a qualquer hora no período' },
];

export const LiberarAcessoModal: React.FC<LiberarAcessoModalProps> = ({
  open,
  customerId,
  unitId,
  unitDisplay,
  unitNumber,
  blockId,
  blockName,
  authorizedByResidentId,
  authorizedByName,
  onClose,
  onCreated,
  lightTheme = false,
}) => {
  // Which branch the operator chose. Showing both at once made the form ask two questions at the
  // same time — "who is this?" twice, in two different ways.
  const [personStep, setPersonStep] = useState<'search' | 'new'>('search');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<any | null>(null);
  const [fullName, setFullName] = useState('');
  const [cpf, setCpf] = useState('');
  const [phone, setPhone] = useState('');
  const [category, setCategory] = useState<LiberacaoCategory>('visitor');
  const [mode, setMode] = useState<LiberacaoMode>('single');
  const [requiresIntercom, setRequiresIntercom] = useState(true);
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [visitTime, setVisitTime] = useState('');
  const [visitSchedule, setVisitSchedule] = useState<VisitDaySchedule[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Refusing is useless if the refusal cannot be seen.
   *
   * The message used to sit at the bottom of this scrolling body: with the form scrolled to the
   * mode picker it was off-screen, so pressing Liberar on an incomplete form looked like pressing
   * a dead button — no spinner, no message, nothing. Now the message goes in the footer, which
   * never scrolls away, and the offending field is scrolled into view and focused.
   */
  const fail = (message: string, fieldId?: string) => {
    setError(message);
    if (!fieldId) return;
    // Synchronously: the field is already on screen (none of them are conditional on the error),
    // so there is nothing to wait a frame for — and a deferred focus outlives the close button.
    const el = fieldId ? (document.getElementById(fieldId) as HTMLElement | null) : null;
    el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    el?.focus({ preventScroll: true });
  };

  // Search-before-register: the person is usually already in the base (a previous visit, another
  // unit's liberação), and registering them again would split their history in two.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!open || selectedPerson) return;
    const term = query.trim();
    if (term.length < 3) {
      setResults(null);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const r = await condoApi.getPeople(customerId, {
          search: term,
          role_type: 'visitor,service_provider',
          page_size: 8,
        } as any);
        if (!cancelled) setResults(r?.data?.data || []);
      } catch {
        // A failed lookup must not block registering someone new.
        if (!cancelled) setResults(null);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, open, selectedPerson, customerId]);

  if (!open) return null;

  const nameParts = fullName.trim().split(/\s+/).filter(Boolean);

  const reset = () => {
    setPersonStep('search'); setQuery(''); setResults(null); setSelectedPerson(null);
    setFullName(''); setCpf(''); setPhone('');
    setCategory('visitor'); setMode('single'); setRequiresIntercom(true);
    setValidFrom(''); setValidUntil(''); setVisitTime(''); setVisitSchedule([]);
    setError(null);
  };

  const close = () => { reset(); onClose(); };

  const submit = async () => {
    if (saving) return;
    setError(null);

    if (!selectedPerson && personStep === 'search') {
      fail('Busque a pessoa ou escolha "Cadastrar nova"', 'liberar-busca');
      return;
    }
    // The shared rule, not a word count: "a a" is two words and was accepted, which is how a
    // pair of single letters ended up on the gate screen as somebody's name. validateFullName
    // also owns the wording, so every name field in the app refuses the same things the same way.
    if (!selectedPerson) {
      const nameProblem = validateFullName(fullName);
      if (nameProblem) {
        fail(nameProblem, 'liberar-nome');
        return;
      }
    }
    const digits = normalizeCPF(cpf);
    if (!selectedPerson && digits && digits.length !== 11) {
      fail('CPF deve ter 11 dígitos', 'liberar-cpf');
      return;
    }

    const draft = {
      mode,
      category,
      requiresIntercom,
      validFrom: validFrom || undefined,
      validUntil: validUntil || undefined,
      visitTime: mode === 'single' ? (visitTime || undefined) : undefined,
      visitSchedule: mode === 'periodo' ? visitSchedule : undefined,
      unitId,
      unitDisplay,
      unitNumber,
      blockId,
      blockName,
      authorizedByResidentId,
      authorizedByName,
    };

    const problem = validateLiberacaoDraft(draft);
    if (problem) {
      fail(problem, fieldForProblem(problem, mode));
      return;
    }

    setSaving(true);
    try {
      // Reuse the person when the CPF is already known, exactly like CreateVisitorForSelf does —
      // otherwise one visitor liberado to two units becomes two people in the base.
      let personId: string | undefined = selectedPerson?.id;
      if (!personId && digits) {
        try {
          const found = await condoApi.searchPersonByCPF(customerId, digits);
          personId = found?.data?.data?.id;
        } catch {
          // 404 just means "not registered yet".
        }
      }

      if (!personId) {
        const created = await condoApi.createPerson(customerId, {
          customer_id: customerId,
          first_name: nameParts[0],
          last_name: nameParts.slice(1).join(' '),
          cpf: digits || undefined,
          phone: phone || undefined,
          is_active: true,
        });
        personId = created?.data?.data?.id;
      }

      if (!personId) throw new Error('Não foi possível registrar a pessoa');

      await condoApi.createPersonRole(customerId, personId, buildLiberacaoRolePayload(draft));
      reset();
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Erro ao liberar acesso');
    } finally {
      setSaving(false);
    }
  };

  const labelCls = cn('block text-[11px] font-semibold uppercase tracking-wide mb-1', lightTheme ? 'text-gray-500' : 'text-gray-400');
  const inputCls = cn(
    'w-full rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500',
    lightTheme ? 'bg-white border border-gray-300 text-gray-900' : 'bg-gray-900 border border-gray-700 text-gray-100',
  );

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4" style={{ zIndex: 9998 }} onClick={close}>
      <div
        role="dialog"
        aria-label="Liberar acesso"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'w-full max-w-lg max-h-[90vh] flex flex-col rounded-xl border shadow-2xl',
          lightTheme ? 'bg-white border-gray-200' : 'bg-gray-950 border-gray-800',
        )}
      >
        <div className={cn('flex items-center justify-between px-4 py-3 border-b', lightTheme ? 'border-gray-200' : 'border-gray-800')}>
          <div>
            <h3 className={cn('text-sm font-semibold', lightTheme ? 'text-gray-900' : 'text-white')}>Liberar acesso</h3>
            {unitDisplay && (
              <p className={cn('text-xs', lightTheme ? 'text-gray-500' : 'text-gray-400')}>{unitDisplay}</p>
            )}
          </div>
          <button type="button" onClick={close} aria-label="Fechar" className={cn('p-1 rounded', lightTheme ? 'text-gray-400 hover:bg-gray-100' : 'text-gray-500 hover:bg-gray-800')}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {(['visitor', 'service_provider'] as LiberacaoCategory[]).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={cn(
                  'rounded-lg border px-3 py-2 text-xs font-semibold transition-colors',
                  category === c
                    ? 'border-blue-500 bg-blue-500/10 text-blue-500'
                    : lightTheme ? 'border-gray-300 text-gray-600 hover:bg-gray-50' : 'border-gray-700 text-gray-400 hover:bg-gray-900',
                )}
              >
                {c === 'visitor' ? 'Visitante' : 'Prestador'}
              </button>
            ))}
          </div>

          {selectedPerson ? (
            <div className={cn('flex items-center gap-2 rounded-lg border px-3 py-2', lightTheme ? 'border-blue-300 bg-blue-50' : 'border-blue-700/50 bg-blue-900/20')}>
              <div className="flex-1 min-w-0">
                <p className={cn('text-sm font-semibold truncate', lightTheme ? 'text-gray-900' : 'text-gray-100')}>
                  {`${selectedPerson.first_name || ''} ${selectedPerson.last_name || ''}`.trim()}
                </p>
                <p className={cn('text-[11px] truncate', lightTheme ? 'text-gray-500' : 'text-gray-400')}>
                  {[selectedPerson.cpf ? formatCPF(selectedPerson.cpf) : null, selectedPerson.phone].filter(Boolean).join(' · ') || 'Já cadastrado'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setSelectedPerson(null); setQuery(''); setResults(null); }}
                className={cn('text-[11px] font-semibold px-2 py-1 rounded border', lightTheme ? 'border-gray-300 text-gray-600 hover:bg-white' : 'border-gray-700 text-gray-300 hover:bg-gray-800')}
              >
                Trocar
              </button>
            </div>
          ) : (
            <>
              {/* Ask which one. Most people are already in the base — registering them again splits
                  one person's history across two records — but a first-timer needs the other path. */}
              <div className="grid grid-cols-2 gap-2">
                {([
                  ['search', 'Buscar cadastrada'],
                  ['new', 'Cadastrar nova'],
                ] as const).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => { setPersonStep(key); setError(null); }}
                    className={cn(
                      'rounded-lg border px-3 py-2 text-xs font-semibold transition-colors',
                      personStep === key
                        ? 'border-blue-500 bg-blue-500/10 text-blue-500'
                        : lightTheme ? 'border-gray-300 text-gray-600 hover:bg-gray-50' : 'border-gray-700 text-gray-400 hover:bg-gray-900',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {personStep === 'search' && (
              <div>
                <label className={labelCls} htmlFor="liberar-busca">Buscar pessoa já cadastrada</label>
                <div className="relative">
                  <Search className={cn('absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none', lightTheme ? 'text-gray-400' : 'text-gray-500')} />
                  <input
                    id="liberar-busca"
                    className={cn(inputCls, 'pl-8')}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Nome ou CPF"
                  />
                  {searching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-blue-500" />}
                </div>

                {results !== null && results.length > 0 && (
                  <div className={cn('mt-1 rounded-lg border divide-y max-h-40 overflow-y-auto', lightTheme ? 'border-gray-200 divide-gray-100' : 'border-gray-800 divide-gray-800')}>
                    {results.map((p: any) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSelectedPerson(p)}
                        className={cn('w-full text-left px-3 py-2 transition-colors', lightTheme ? 'hover:bg-gray-50' : 'hover:bg-gray-900')}
                      >
                        <p className={cn('text-xs font-semibold truncate', lightTheme ? 'text-gray-900' : 'text-gray-100')}>
                          {`${p.first_name || ''} ${p.last_name || ''}`.trim()}
                        </p>
                        {(p.cpf || p.phone) && (
                          <p className={cn('text-[11px] truncate', lightTheme ? 'text-gray-500' : 'text-gray-400')}>
                            {[p.cpf ? formatCPF(p.cpf) : null, p.phone].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {results !== null && results.length === 0 && !searching && (
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <p className={cn('text-[11px]', lightTheme ? 'text-gray-500' : 'text-gray-500')}>
                      Nenhuma pessoa encontrada.
                    </p>
                    <button
                      type="button"
                      onClick={() => setPersonStep('new')}
                      className="text-[11px] font-semibold text-blue-500 hover:underline"
                    >
                      Cadastrar nova pessoa
                    </button>
                  </div>
                )}
              </div>
              )}

              {personStep === 'new' && (
              <>
              <div>
                <label className={labelCls} htmlFor="liberar-nome">Nome completo *</label>
                <input id="liberar-nome" className={inputCls} value={fullName} onChange={(e) => setFullName(sanitizePersonName(e.target.value))} placeholder="Nome e sobrenome" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls} htmlFor="liberar-cpf">CPF</label>
                  <input id="liberar-cpf" className={inputCls} value={cpf} onChange={(e) => setCpf(formatCPF(e.target.value))} placeholder="000.000.000-00" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="liberar-telefone">Telefone</label>
                  <input id="liberar-telefone" className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(00) 00000-0000" />
                </div>
              </div>
              </>
              )}
            </>
          )}

          <div>
            <span className={labelCls}>Tipo de liberação</span>
            <div className="space-y-1.5">
              {MODES.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMode(m.key)}
                  className={cn(
                    'w-full text-left rounded-lg border px-3 py-2 transition-colors',
                    mode === m.key
                      ? 'border-blue-500 bg-blue-500/10'
                      : lightTheme ? 'border-gray-200 hover:bg-gray-50' : 'border-gray-800 hover:bg-gray-900',
                  )}
                >
                  <p className={cn('text-xs font-semibold', mode === m.key ? 'text-blue-500' : lightTheme ? 'text-gray-900' : 'text-gray-200')}>{m.label}</p>
                  <p className={cn('text-[11px]', lightTheme ? 'text-gray-500' : 'text-gray-500')}>{m.hint}</p>
                </button>
              ))}
            </div>
          </div>

          {mode === 'periodo' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls} htmlFor="liberar-inicio">Início</label>
                  <input id="liberar-inicio" type="date" className={inputCls} value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="liberar-fim">Fim *</label>
                  <input id="liberar-fim" type="date" className={inputCls} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
                </div>
              </div>
              <div>
                <span className={labelCls}>Grade de horário</span>
                <VisitGradeInput value={visitSchedule} onChange={setVisitSchedule} darkMode={!lightTheme} />
              </div>
            </>
          )}

          {mode === 'irrestrito' && (
            <div>
              <label className={labelCls} htmlFor="liberar-ate">Válido até (opcional)</label>
              <input id="liberar-ate" type="date" className={inputCls} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
            </div>
          )}

          {/* A visita única is an appointment: "o pintor vem quinta às 14h". The hour is half of
              what the resident told the portaria — it does not restrict entry (the day does), it
              tells the porteiro who is expected and when. */}
          {mode === 'single' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls} htmlFor="liberar-dia">Dia da visita</label>
                <input id="liberar-dia" type="date" className={inputCls} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
              </div>
              <div>
                <label className={labelCls} htmlFor="liberar-hora">Horário previsto</label>
                <input id="liberar-hora" type="time" className={inputCls} value={visitTime} onChange={(e) => setVisitTime(e.target.value)} />
              </div>
              <p className={cn('col-span-2 -mt-1 text-[11px]', lightTheme ? 'text-gray-500' : 'text-gray-500')}>
                Sem dia, vale até ser usada. O horário é informativo — a entrada continua liberada o dia todo.
              </p>
            </div>
          )}

          <button
            type="button"
            onClick={() => setRequiresIntercom((v) => !v)}
            className={cn(
              'w-full flex items-center justify-between rounded-lg border px-3 py-2',
              lightTheme ? 'border-gray-200' : 'border-gray-800',
            )}
          >
            <span className="text-left">
              <span className={cn('block text-xs font-semibold', lightTheme ? 'text-gray-900' : 'text-gray-200')}>Interfonar ao chegar</span>
              <span className={cn('block text-[11px]', lightTheme ? 'text-gray-500' : 'text-gray-500')}>
                Avisar o morador por interfone quando chegar na portaria
              </span>
            </span>
            <span
              role="switch"
              aria-checked={requiresIntercom}
              aria-label="Interfonar ao chegar"
              className={cn(
                'relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors',
                requiresIntercom ? 'bg-amber-500' : lightTheme ? 'bg-gray-300' : 'bg-gray-700',
              )}
            >
              <span className={cn('pointer-events-none inline-block h-4 w-4 mt-0.5 ml-0.5 rounded-full bg-white shadow transition', requiresIntercom && 'translate-x-4')} />
            </span>
          </button>

        </div>

        {/* Footer, not the scrolling body: see `fail` — a refusal rendered below the fold is
            indistinguishable from a button that does nothing. */}
        <div data-testid="liberar-footer" className={cn('px-4 py-3 border-t', lightTheme ? 'border-gray-200' : 'border-gray-800')}>
          {error && (
            <p
              role="alert"
              className={cn(
                'mb-2 flex items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium',
                lightTheme ? 'bg-red-50 border-red-200 text-red-700' : 'bg-red-500/10 border-red-500/30 text-red-400',
              )}
            >
              <AlertTriangle className="h-3.5 w-3.5 mt-px flex-shrink-0" />
              <span>{error}</span>
            </p>
          )}
          <div className="flex justify-end gap-2">
          <button type="button" onClick={close} className={cn('text-xs px-3 py-2 rounded-lg border', lightTheme ? 'border-gray-300 text-gray-600' : 'border-gray-700 text-gray-300')}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="text-xs font-semibold px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60 flex items-center gap-2"
          >
            {saving && <Loader2 className="h-3 w-3 animate-spin" />}
            Liberar
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiberarAcessoModal;
